/**
 * Navigation utilities for consistent state management across pages.
 *
 * This module provides utilities for:
 * - Building navigation state objects
 * - Retrieving back paths from location state
 * - Custom hook for navigation context
 */

/**
 * Build navigation state for passing between pages.
 *
 * @param {string} from - Origin page identifier
 * @param {string} returnPath - Path to return to
 * @param {Object} extras - Additional state properties
 * @param {Object} [extras.returnState] - State to restore on return
 * @param {Array} [extras.breadcrumbs] - Breadcrumb trail
 * @param {Object} [extras.message] - Feedback message
 * @returns {Object} Navigation state object
 *
 * @example
 * const state = buildNavState('clients-list', '/clients', {
 *   returnState: { filter: 'active', page: 2 }
 * });
 */
export function buildNavState(from, returnPath, extras = {}) {
  return {
    from,
    returnPath,
    returnState: {
      scrollY: typeof window !== 'undefined' ? window.scrollY : 0,
      ...extras.returnState
    },
    ...extras
  };
}

/**
 * Get the back path from location state with fallback.
 *
 * @param {Object} location - React Router location object
 * @param {string} fallback - Default path if no state exists
 * @returns {string} The path to navigate back to
 *
 * @example
 * const backPath = getBackPath(location, '/validation-hub');
 */
export function getBackPath(location, fallback) {
  return location?.state?.returnPath || fallback;
}

/**
 * Get return state from location for restoring previous page state.
 *
 * @param {Object} location - React Router location object
 * @returns {Object|null} The return state or null
 */
export function getReturnState(location) {
  return location?.state?.returnState || null;
}

/**
 * Get breadcrumbs from location state.
 *
 * @param {Object} location - React Router location object
 * @returns {Array|null} Breadcrumb items or null
 */
export function getBreadcrumbs(location) {
  return location?.state?.breadcrumbs || null;
}

/**
 * Get message from location state (for success/error feedback).
 *
 * @param {Object} location - React Router location object
 * @returns {Object|null} Message object {type, text} or null
 */
export function getMessage(location) {
  return location?.state?.message || null;
}

/**
 * Build breadcrumb trail for a page.
 *
 * @param {Array} items - Array of {label, to} objects
 * @returns {Array} Formatted breadcrumb items
 *
 * @example
 * const breadcrumbs = buildBreadcrumbs([
 *   { label: 'Validare', to: '/validation-hub' },
 *   { label: 'Emitenți', to: '/clients' },
 *   { label: 'Client ABC', to: null }  // Current page
 * ]);
 */
export function buildBreadcrumbs(items) {
  return items.map((item, index) => ({
    ...item,
    isCurrent: index === items.length - 1 || item.to === null
  }));
}

/**
 * Restore scroll position from navigation state.
 * Should be called in useEffect on page mount.
 *
 * @param {Object} location - React Router location object
 */
export function restoreScrollPosition(location) {
  const returnState = getReturnState(location);
  if (returnState?.scrollY) {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      window.scrollTo(0, returnState.scrollY);
    }, 0);
  }
}
